import React, { useState } from 'react';
import { CourseDTO, ForfeitureRequestDTO, RplDTO } from '../../services/apiClient';
import { useForfeitureReasons } from '../../hooks/useForfeitureReasonApi';
import { useFileApi } from '../../hooks/useFileApi';
import { useCourseApi } from '../../hooks/useCourseApi';
import { useRplApi } from '../../hooks/useRplApi';
import { SignatureCapture } from '../common/SignatureCapture';
import FormField from '../common/FormField';
import Button from '../common/Button';
import { base64ToFile } from '../../utils/fileUtils';
import { showAlert } from '../../utils/sweetAlertUtils';

const OTHER_REASON_ID = 'a1000001-0000-0000-0000-000000000008';

interface ForfeitureDrawerProps {
  item: (CourseDTO | RplDTO) & { itemType: 'course' | 'rpl' };
  onSuccess: () => void;
  onCancel: () => void;
}

const ForfeitureDrawer: React.FC<ForfeitureDrawerProps> = ({ item, onSuccess, onCancel }) => {
  const [forfeitureReasonId, setForfeitureReasonId] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [signedAtLocation, setSignedAtLocation] = useState('');
  const [hasSection16Licence, setHasSection16Licence] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: forfeitureReasons = [] } = useForfeitureReasons();
  const { uploadFile, isLoading: isUploading } = useFileApi();
  const { forfeit: forfeitCourse } = useCourseApi();
  const { forfeit: forfeitRpl } = useRplApi();

  const courseTypeTitle = item.courseType?.title ?? 'Dedicated Status';
  const isOtherSelected = forfeitureReasonId.toLowerCase() === OTHER_REASON_ID;

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!forfeitureReasonId) newErrors.forfeitureReasonId = 'Please select a reason.';
    if (isOtherSelected && !otherReason.trim()) newErrors.otherReason = 'Please specify the reason.';
    if (!signedAtLocation.trim()) newErrors.signedAtLocation = 'Please enter the location.';
    if (!signatureData) newErrors.signature = 'Please provide your signature.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>) => {
    setForfeitureReasonId(e.target.value);
    if (e.target.value.toLowerCase() !== OTHER_REASON_ID) {
      setOtherReason('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      let signatureFileId: string | undefined;

      if (signatureData) {
        const filename = `forfeiture-signature-${item.id}-${Date.now()}.png`;
        const file = base64ToFile(signatureData, filename);
        const uploadResponse = await uploadFile('forfeiture-signatures', filename, file);

        if (!uploadResponse?.success || !uploadResponse.data) {
          await showAlert({ title: 'Error', text: 'Failed to upload signature. Please try again.', icon: 'error', confirmButtonText: 'OK' });
          return;
        }
        signatureFileId = uploadResponse.data;
      }

      const dto: ForfeitureRequestDTO = {
        forfeitureReasonId,
        forfeitureDate: new Date(),
        forfeitureSignedAtLocation: signedAtLocation,
        forfeitureSignatureFileId: signatureFileId,
        forfeitureHasSection16Licence: hasSection16Licence,
        forfeitureOtherReason: isOtherSelected ? otherReason : undefined,
      };

      if (item.itemType === 'course') {
        await forfeitCourse(item.id!, dto);
      } else {
        await forfeitRpl(item.id!, dto);
      }

      await showAlert({ title: 'Forfeited', text: `Your ${courseTypeTitle} dedicated status has been forfeited.`, icon: 'success', confirmButtonText: 'OK' });
      onSuccess();
    } catch {
      await showAlert({ title: 'Error', text: 'An error occurred. Please try again.', icon: 'error', confirmButtonText: 'OK' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col space-y-2">
      <div className="p-4 rounded bg-red-50 dark:bg-red-900/20 mb-2">
        <p className="text-sm text-red-800 dark:text-red-200">
          You're about to forfeit your <strong>{courseTypeTitle}</strong> status. Once you confirm, this record is permanently forfeited and can't be undone. Keep in mind that you can always re-apply later to have your NHSA dedicated hunter and/or sports-person status re-instated, by paying the usual administration fee for being awarded dedicated status on the grounds of RPL.
        </p>
      </div>

      <FormField
        id="forfeitureReasonId"
        label="Reason for Forfeiture"
        type="dropdown"
        required
        value={forfeitureReasonId}
        onChange={handleReasonChange}
        placeholder="Select a reason..."
        options={forfeitureReasons.map((r) => ({ value: r.id ?? '', label: r.title ?? '' }))}
        error={errors.forfeitureReasonId}
      />

      {isOtherSelected && (
        <FormField
          id="otherReason"
          label="Please specify"
          type="textarea"
          required
          value={otherReason}
          onChange={(e) => setOtherReason(e.target.value)}
          placeholder="Please provide details..."
          error={errors.otherReason}
          rows={3}
        />
      )}

      <FormField
        id="hasSection16Licence"
        label="Do you currently hold a valid Section 16 firearm licence in your name?"
        type="checkbox"
        value={hasSection16Licence}
        onChange={(e) => setHasSection16Licence((e.target as HTMLInputElement).checked)}
      />

      <FormField
        id="signedAtLocation"
        label="Location"
        required
        value={signedAtLocation}
        onChange={(e) => setSignedAtLocation(e.target.value)}
        placeholder="City / Town where this is being signed"
        error={errors.signedAtLocation}
      />

      <SignatureCapture
        onSave={setSignatureData}
        onClear={() => setSignatureData(null)}
        error={errors.signature}
      />

      <div className="flex gap-3 pt-2">
        <Button
          type="submit"
          className="flex-1"
          disabled={submitting || isUploading}
          isLoading={submitting || isUploading}
        >
          Confirm Forfeiture
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="flex-1"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default ForfeitureDrawer;
